import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/api/constants";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";
import type { PaginationParams } from "@/types/api";
import { useAdminCurrency } from "@/utils/hooks/useAdminCurrency";

import { fundingApi } from "./fundingApi";
import type {
  IUpdateFundingCreditPeriodRequest,
  IUpdateFundingTermsRequest,
  IUpdateTransferRequest,
  PayoutGroup,
} from "./types";

export const useGetAllFundingsQuery = (
  groupId: string,
  params?: PaginationParams,
  enabled: boolean = true
) => {
  const currency = useAdminCurrency();
  return useQuery({
    queryKey: [QUERY_KEYS.groupFunding, params, currency],
    queryFn: () => fundingApi.getAllFundings(groupId, params),
    enabled: enabled && !!groupId,
  });
};

export const useCreateFundingMutation = () => {
  return useMutation({
    mutationFn: (data: PayoutGroup) => {
      return fundingApi.postFunding(data);
    },
  });
};

export const useGetFundingApplicationBrandQuery = (
  brandId: string,
  params?: PaginationParams
) => {
  const currency = useAdminCurrency();
  return useQuery({
    queryKey: [QUERY_KEYS.brandFundingApplication, params, currency],
    queryFn: () => fundingApi.getFundingApplication(brandId, params),
  });
};

export const useGetFundingConsumptionBrandQuery = (
  brandId: string,
  params?: PaginationParams,
  enabled: boolean = true
) => {
  const currency = useAdminCurrency();
  return useQuery({
    queryKey: [QUERY_KEYS.brandFundingConsumption, params, currency],
    queryFn: () => fundingApi.getFundingConsumption(brandId, params),
    enabled: enabled && !!brandId,
  });
};

export const useGetApplicationFundingByIdQuery = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.brandFundingApplication, id],
    queryFn: () => fundingApi.getApplicationById(id),
    meta: RESOURCE_QUERY_META,
  });
};

export const useUpdateFundingTermsMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateFundingTermsRequest;
    }) => {
      return fundingApi.updateFundingTerms(id, data);
    },
  });
};

export const useApproveFundingMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return fundingApi.approveFunding(id);
    },
  });
};

export const useUpdateFundingCreditPeriodMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateFundingCreditPeriodRequest;
    }) => {
      return fundingApi.updateFundingCreditPeriod(id, data);
    },
  });
};

export const useAddTransferDetailsMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateTransferRequest;
    }) => {
      return fundingApi.addTransferDetails(id, data);
    },
  });
};

export const useRejectFundingMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return fundingApi.rejectFunding(id);
    },
  });
};

export const useGetFundingConsumptionGroupQuery = (
  groupId: string,
  params?: PaginationParams,
  enabled: boolean = true
) => {
  const currency = useAdminCurrency();
  return useQuery({
    queryKey: [QUERY_KEYS.groupFundingConsumption, params, currency],
    queryFn: () => fundingApi.getFundingGroupConsumption(groupId, params),
    enabled: enabled && !!groupId,
  });
};

export const useGetFundingBrandStatByIdQuery = (
  id: string,
  params: { startDate: string; endDate: string }
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.brandFundingStatistic, id, params],
    queryFn: () => fundingApi.getFundingStatisticById(id, params),
    enabled: !!id && !!params,
  });
};
